import { createNotification } from "#src/common/system/notifications";
import { createToast } from "#src/common/system/toasts";
import { ButtonV2 } from "#src/commonV2/Button";
import { FormInput } from "#src/commonV2/form/Input/FormInput";
import { useFormInput } from "#src/commonV2/form/Input/useFormInput";
import { LoaderV2 } from "#src/commonV2/LoadingState";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import constants from "#src/config/constants";
import dlpService from "#src/services/endpoint/dlp";
import { BackupStorageName } from "#src/services/endpoint/dlp/enums";
import { IBackupSettingResponse } from "#src/services/endpoint/dlp/types";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface IEndpointDlpS3ConfigModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  initSettings: IBackupSettingResponse | null;
}

export const EndpointDlpS3ConfigModal = ({
  workspaceId,
  open,
  onClose,
  onRefresh,
  initSettings,
}: IEndpointDlpS3ConfigModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "dlp.backupSettings.backupStorage.s3.modal",
  });

  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  // --------------- HOOKS ---------------

  const {
    value: accessKeyId,
    reset: resetAccessKeyIdField,
    renderProps: accessKeyIdFieldRenderProps,
  } = useFormInput({
    label: t("accessKeyId.label"),
    placeholder: t("accessKeyId.placeholder"),
    validation: { required: true },
    defaultValue: initSettings?.storage_access_id ?? undefined,
  });
  const {
    value: accessKeySecret,
    reset: resetAccessKeySecretField,
    renderProps: accessKeySecretFieldRenderProps,
  } = useFormInput({
    label: t("accessKeySecret.label"),
    placeholder: t("accessKeySecret.placeholder"),
    validation: { required: true },
    defaultValue: initSettings?.storage_secret_key ?? undefined,
  });
  const {
    value: awsRegion,
    reset: resetAwsRegionField,
    renderProps: awsRegionFieldRenderProps,
  } = useFormInput({
    label: t("awsRegion.label"),
    placeholder: t("awsRegion.placeholder"),
    validation: { required: true },
    defaultValue: initSettings?.storage_region ?? undefined,
  });
  const {
    value: awsBucket,
    reset: resetAwsBucketField,
    renderProps: awsBucketFieldRenderProps,
  } = useFormInput({
    label: t("awsBucket.label"),
    placeholder: t("awsBucket.placeholder"),
    validation: { required: true },
    defaultValue: initSettings?.storage_bucket ?? undefined,
  });
  const {
    value: amazonResourceName,
    reset: resetAmazonResourceNameField,
    renderProps: amazonResourceNameFieldRenderProps,
  } = useFormInput({
    label: t("amazonResourceName.label"),
    placeholder: t("amazonResourceName.placeholder"),
    validation: { required: true },
    tooltip: t("amazonResourceName.tooltip"),
    defaultValue: initSettings?.storage_arn ?? undefined,
  });

  // --------------- METHODS ---------------

  const onSubmitChangeS3Config = () => {
    if (accessKeyId.length === 0) {
      createToast({ type: "warning", message: t("accessKeyId.required") });
      return;
    }
    if (accessKeySecret.length === 0) {
      createToast({ type: "warning", message: t("accessKeySecret.required") });
      return;
    }
    if (awsRegion.length === 0) {
      createToast({ type: "warning", message: t("awsRegion.required") });
      return;
    }
    if (awsBucket.length === 0) {
      createToast({ type: "warning", message: t("awsBucket.required") });
      return;
    }
    if (amazonResourceName.length === 0) {
      createToast({
        type: "warning",
        message: t("amazonResourceName.required"),
      });
      return;
    }

    setSubmitting(true);
    dlpService
      .updateBackupSettings(workspaceId, {
        storage: BackupStorageName.S3,
        storage_access_id: accessKeyId,
        storage_secret_key: accessKeySecret,
        storage_region: awsRegion,
        storage_bucket: awsBucket,
        storage_arn: amazonResourceName,
      })
      .then((response) => {
        if (response.success) {
          onRefresh();
          onCloseAndCleanup();
          createNotification({
            type: "success",
            message: t("notification.success.message"),
            detail: t("notification.success.detail"),
          });
        } else {
          createNotification({
            type: "error",
            message: t("notification.failed.message", { name: response.error }),
            detail: (
              <>
                {t("notification.failed.detail")}{" "}
                <a
                  href={`mailto:${constants.CYSTACK_SUPPORT}`}
                  className="no-underline font-semibold text-brand-v2-300 hover:text-brand-v2-400"
                >
                  {constants.CYSTACK_SUPPORT}
                </a>
              </>
            ),
          });
        }
        setSubmitting(false);
      })
      .catch(apiErrorHandler);
  };

  const onCloseAndCleanup = () => {
    resetAccessKeyIdField();
    resetAccessKeySecretField();
    resetAwsRegionField();
    resetAwsBucketField();
    resetAmazonResourceNameField();
    setSubmitting(false);
    onClose();
  };

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onCloseAndCleanup} className="w-[50rem]">
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      <div className="flex flex-col gap-6">
        <span className="text-md text-gray-v2-600">
          {t("hint1")}
          <Link
            to={
              "https://cystack.notion.site/H-ng-d-n-c-u-h-nh-Kho-l-u-tr-AWS-S3-cho-Backup-Endpoint-1e0fb45532ff8073878df849906352ea"
            }
            target="_blank"
            className="no-underline font-semibold"
          >
            {t("thisGuide")}
          </Link>
        </span>
        <span className="text-md text-gray-v2-600">{t("hint2")}</span>
        <div className="grid grid-cols-2 gap-6">
          <FormInput {...accessKeyIdFieldRenderProps} />
          <FormInput {...accessKeySecretFieldRenderProps} />
          <FormInput {...awsRegionFieldRenderProps} />
          <FormInput {...awsBucketFieldRenderProps} />
          <FormInput {...amazonResourceNameFieldRenderProps} />
        </div>
        <div className="flex justify-end gap-2">
          <ButtonV2
            variant="secondary"
            color="gray"
            onClick={onCloseAndCleanup}
          >
            {t("button.cancel")}
          </ButtonV2>
          <ButtonV2 onClick={onSubmitChangeS3Config} disabled={isSubmitting}>
            {isSubmitting ? <LoaderV2 baseSize={1} /> : t("button.save")}
          </ButtonV2>
        </div>
      </div>
    </ModalV2>
  );
};
